export function uint8ArrayToHex(uint8Array: Uint8Array): string {
  let hex = "0x";
  for (const element of uint8Array) {
    const byte = element;
    hex += (byte < 16 ? "0" : "") + byte.toString(16);
  }
  return hex;
}

export function hexToUint8Array(hex: string): Uint8Array {
  if (hex.length % 2 !== 0) {
    throw new Error("Hex string must have an even number of characters.");
  }

  if (hex.startsWith("0x")) {
    hex = hex.substring(2);
  }

  const uint8Array = new Uint8Array(hex.length / 2);
  for (let i = 0; i < hex.length; i += 2) {
    const byte = parseInt(hex.substring(i, i + 2), 16);
    if (isNaN(byte)) {
      throw new Error("Invalid hexadecimal string.");
    }
    uint8Array[i / 2] = byte;
  }

  return uint8Array;
}

export function hexToArrayBuffer(hex: string): ArrayBuffer {
  // Remove the optional '0x' prefix if it exists
  if (hex.startsWith("0x") || hex.startsWith("0X")) {
    hex = hex.slice(2);
  }

  // Ensure the hex string has an even number of characters
  if (hex.length % 2 !== 0) {
    throw new Error("Invalid hex string: length must be even");
  }

  // Validate hexadecimal characters
  if (!/^[\da-fA-F]+$/.test(hex)) {
    throw new Error("Invalid hex string: contains non-hexadecimal characters");
  }

  const byteLength = hex.length / 2;
  const buffer = new ArrayBuffer(byteLength);
  const view = new Uint8Array(buffer); // Revert to Uint8Array for simplicity

  for (let i = 0; i < byteLength; i++) {
    // Use `slice` instead of deprecated `substr`
    const byteHex = hex.slice(i * 2, i * 2 + 2);
    view[i] = parseInt(byteHex, 16);
  }

  return buffer;
}

export function stringToUint8Array(inputString: string): Uint8Array {
  const encoder = new TextEncoder();
  return encoder.encode(inputString);
}

export function uint8ArrayToString(uint8Array: Uint8Array): string {
  const decoder = new TextDecoder("utf-8");
  return decoder.decode(uint8Array);
}

export async function fileToUint8Array(file: File): Promise<Uint8Array> {
  return new Promise<Uint8Array>((resolve, reject) => {
    const reader = new FileReader();

    reader.onload = (event) => {
      if (event.target && event.target.result instanceof ArrayBuffer) {
        const arrayBuffer = event.target.result;
        const uint8Array = new Uint8Array(arrayBuffer);
        resolve(uint8Array);
      } else {
        reject(new Error("Failed to read file as ArrayBuffer"));
      }
    };

    reader.onerror = (event) => {
      reject(new Error(`FileReader error: ${event.target?.error}`));
    };

    reader.readAsArrayBuffer(file);
  });
}

export const blobToUint8Array = (blob: Blob): Promise<Uint8Array> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      if (reader.result instanceof ArrayBuffer) {
        resolve(new Uint8Array(reader.result));
      } else {
        reject(new Error("Failed to convert Blob to Uint8Array"));
      }
    };
    reader.onerror = () => {
      reject(new Error("Failed to convert Blob to Uint8Array"));
    };
    reader.readAsArrayBuffer(blob);
  });
};

export const uint8ArrayToBlob = (uint8Array: Uint8Array): Blob => {
  return new Blob([uint8Array]);
};

export const blobToBase64 = (blob: Blob | null): Promise<string> => {
  if (!blob) {
    return Promise.resolve("");
  }
  return new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.onload = () => {
      if (typeof reader.result === "string") {
        resolve(reader.result);
      } else {
        reject(new Error("Failed to convert Blob to Base64 string"));
      }
    };

    reader.onerror = () => {
      reject(new Error("Failed to convert Blob to Base64 string"));
    };

    reader.readAsDataURL(blob);
  });
};

export const isHexadecimal = (str: string): boolean => {
  // This regex accepts an optional "0x" or "0X" prefix,
  // followed by one or more hexadecimal digits.
  const hexRegex = /^(0[xX])?[0-9a-fA-F]+$/;
  return hexRegex.test(str);
};

/**
 * Checks whether a string looks like a base64-encoded ciphertext.
 * Requires at least one character and only valid base64 characters.
 */
export const isBase64 = (str: string): boolean => {
  if (str.length === 0) {
    return false;
  }
  return /^[A-Za-z0-9+/]+=*$/.test(str);
};

/**
 * Checks whether a string looks like encrypted content — either
 * legacy hex-encoded or current base64-encoded ciphertext.
 */
export const isCiphertext = (str: string): boolean => {
  return isHexadecimal(str) || isBase64(str);
};

export function uint8ArrayToBase64(uint8Array: Uint8Array): string {
  const chunkSize = 0x8000; // 32KB chunks
  const chunks: string[] = [];
  for (let i = 0; i < uint8Array.byteLength; i += chunkSize) {
    const subarray = uint8Array.subarray(i, i + chunkSize);
    chunks.push(String.fromCharCode.apply(null, Array.from(subarray)));
  }
  const binary = chunks.join("");
  return btoa(binary);
}

export function base64ToUint8Array(base64: string): Uint8Array {
  const binary = atob(base64);
  const uint8Array = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) {
    uint8Array[i] = binary.charCodeAt(i);
  }
  return uint8Array;
}
